/* Calabasas Quiz Engine v2 — pure recommendation core. Score, never filter.
   Runs identically in the browser (window.SQEngine) and Node (module.exports).
   Only stock-outs and a declared nut allergy exclude a product; everything else is a score.
   Spec: specs/quiz-engine-v2.md (weights table). */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.SQEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ROUTINE = ['cleanser', 'toner', 'serum', 'moisturizer', 'sunscreen'];
  var ESSENTIAL = ['cleanser', 'serum', 'sunscreen'];
  var SIMPLE = ['cleanser', 'toner', 'moisturizer']; // Simple 3 — the classic beginner routine (round 3)
  // Boosters per primary concern; anti-aging and dark spots get two (the eye pick + the mask pick).
  var BOOSTER_MAP = { 'anti-aging': ['eye-care', 'mask'], 'dark-spots': ['eye-care', 'mask'], hydration: ['mask'], soothing: ['mask'], acne: ['mask'], pores: ['mask'] };
  var CONCERN_PHRASE = { acne: 'calming breakouts', 'dark-spots': 'fading dark spots', hydration: 'deep hydration', soothing: 'soothing redness', 'anti-aging': 'smoothing fine lines', pores: 'clearer pores', brightening: 'a brighter tone', texture: 'smoother texture' };
  var TYPE_WORD = { dry: 'dry', oily: 'oily', combination: 'combination', normal: 'balanced' };

  function mfOf(p) { return p.mf || {}; }
  function has(p, tag) { return p.tags.indexOf(tag) > -1; }

  /* Normalize a suitable-for-skin-type entry (metaobject handle or label) to our vocab. */
  var ALL_TYPES = ['dry', 'oily', 'combination', 'normal', 'sensitive'];
  function normType(s) {
    s = String(s).toLowerCase();
    if (s.indexOf('all') > -1) return '*'; // Shopify "All skin types" metaobject (handle all-skin-types)
    for (var i = 0; i < ALL_TYPES.length; i++) if (s.indexOf(ALL_TYPES[i]) > -1) return ALL_TYPES[i];
    return null;
  }
  function suitableTypes(p) {
    var m = mfOf(p);
    var raw = m.suitable_types || [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var t = normType(raw[i]);
      if (t === '*') return ALL_TYPES.slice();
      if (t && out.indexOf(t) < 0) out.push(t);
    }
    return out;
  }

  /* Score one product for one step. Returns null only for hard exclusions. */
  function scoreProduct(p, step, ctx) {
    if (ctx.nutAllergy && has(p, 'caution:nut-allergen')) return null; // declared allergen — the one hard exclusion besides stock
    var s = 0, sig = [];

    // -- skin type fit
    if (has(p, 'skin:' + ctx.type)) { s += 3; sig.push('type-tag'); }
    else if (suitableTypes(p).indexOf(ctx.type) > -1) { s += 1; sig.push('type-mf'); }
    else { s -= 2; sig.push('type-miss'); }

    // -- concern fit (primary full weight, secondary half)
    var isTreat = (step === 'serum');
    for (var ci = 0; ci < ctx.concerns.length; ci++) {
      var c = ctx.concerns[ci], half = ci > 0 ? 0.5 : 1;
      var wTag = isTreat ? 4 : 1, wSuit = isTreat ? 2 : 1;
      var m = mfOf(p);
      if (has(p, 'concern:' + c)) { s += wTag * half; sig.push((isTreat ? 'treat-tag:' : 'concern-tag:') + c); }
      else if ((m.suitable_for || []).indexOf(c) > -1) { s += wSuit * half; sig.push('suitable:' + c); }
      if ((m.avoid_for || []).indexOf(c) > -1) { s -= 5 * half; sig.push('avoid:' + c); }
      if (c === 'acne' && has(p, 'skin:acne-prone')) { s += 2 * half; sig.push('acne-prone'); }
    }

    // -- sensitivity cautions (scored, not filtered; patch note added at pick time)
    if (ctx.sensitive) {
      if (has(p, 'caution:fragrance')) { s -= 1; sig.push('caution:fragrance'); }
      if (has(p, 'caution:essential-oil')) { s -= 1.5; sig.push('caution:essential-oil'); }
      if (has(p, 'caution:drying-alcohol')) { s -= 2; sig.push('caution:drying-alcohol'); }
      if (has(p, 'caution:strong-active')) { s -= 3; sig.push('caution:strong-active'); }
    }

    // -- texture (quiz.weight) vs type/concern
    var w = mfOf(p).weight;
    if (w) {
      var oilyCtx = ctx.type === 'oily' || ctx.concerns.indexOf('acne') > -1 || ctx.concerns.indexOf('pores') > -1;
      if (oilyCtx) { if (w === 'light') { s += 2; sig.push('weight-light'); } if (w === 'rich') { s -= 3; sig.push('weight-rich-penalty'); } }
      if (ctx.type === 'dry' && w === 'rich') { s += 2; sig.push('weight-rich-fit'); }
      if ((ctx.type === 'combination' || ctx.type === 'normal') && (w === 'light' || w === 'medium')) { s += 1; sig.push('weight-mid-fit'); }
    }

    // -- Q8 ingredient love / avoid
    if (ctx.love && has(p, 'ingredient:' + ctx.love)) { s += 2; sig.push('love:' + ctx.love); }
    if (ctx.avoid) {
      var avoidTag = ctx.avoid.indexOf(':') > -1 ? ctx.avoid : (has(p, 'ingredient:' + ctx.avoid) ? 'ingredient:' + ctx.avoid : 'caution:' + ctx.avoid);
      if (has(p, avoidTag)) { s -= 6; sig.push('avoided'); }
    }

    // -- merchandising signals
    var pr = mfOf(p).priority;
    if (typeof pr === 'number' && pr > 0) { s += Math.min(pr, 3); sig.push('priority:' + pr); }
    if ((p.rc || 0) >= 20) { s += 0.5; sig.push('reviews'); }

    return { p: p, score: s, signals: sig };
  }

  /* Deterministic order: score desc → priority desc → review count desc → handle asc. */
  function order(a, b) {
    if (b.score !== a.score) return b.score - a.score;
    var pa = mfOf(a.p).priority || 0, pb = mfOf(b.p).priority || 0;
    if (pb !== pa) return pb - pa;
    var ra = a.p.rc || 0, rb = b.p.rc || 0;
    if (rb !== ra) return rb - ra;
    return a.p.handle < b.p.handle ? -1 : 1;
  }

  function pickStep(step, ctx, products) {
    var pool = products.filter(function (p) { return has(p, 'step:' + step); });
    var scored = [], restocking = [];
    for (var i = 0; i < pool.length; i++) {
      var p = pool[i];
      if (!p.available) { restocking.push(p); continue; }
      var r = scoreProduct(p, step, ctx);
      if (r) scored.push(r);
    }
    scored.sort(order);
    var pick = scored.length ? scored[0] : null;
    var patchNote = false;
    if (pick && ctx.sensitive && (has(pick.p, 'caution:fragrance') || has(pick.p, 'caution:essential-oil'))) patchNote = true;
    // swaps stay inside the scored, non-negative set
    var alts = scored.slice(1).filter(function (r) { return r.score >= 0; });
    return {
      step: step,
      pick: pick ? pick.p : null,
      score: pick ? pick.score : null,
      signals: pick ? pick.signals : [],
      patchNote: patchNote,
      alternatives: alts.map(function (r) { return r.p; }),
      restocking: restocking,
      why: pick ? whyLine(step, pick, ctx, patchNote) : ''
    };
  }

  /* The concern named by the FIRST signal of the given kind that fired — never ctx.concerns[0].
     Signals are pushed in concern order (primary first), so the primary wins when both fired. */
  function firedConcern(sig, prefix) {
    for (var i = 0; i < sig.length; i++) if (sig[i].indexOf(prefix) === 0) return sig[i].slice(prefix.length);
    return null;
  }
  function phrase(c) { return CONCERN_PHRASE[c] || c; }

  /* Why-this copy — built ONLY from signals that fired, naming ONLY the concern that fired. */
  function whyLine(step, scored, ctx, patchNote) {
    var sig = scored.signals, p = scored.p;
    var tw = TYPE_WORD[ctx.type] || 'your';
    var treatC = firedConcern(sig, 'treat-tag:');
    var suitC = firedConcern(sig, 'suitable:');
    var concC = firedConcern(sig, 'concern-tag:');
    var typeFit = sig.indexOf('type-tag') > -1 || sig.indexOf('type-mf') > -1;
    var love = sig.filter(function (s) { return s.indexOf('love:') === 0; })[0];
    var light = sig.indexOf('weight-light') > -1;
    var richFit = sig.indexOf('weight-rich-fit') > -1;
    var parts = [];

    if (step === 'serum') {
      if (treatC) parts.push('Your treat step — made for ' + phrase(treatC));
      else if (suitC) parts.push('A serum that fits a ' + phrase(suitC) + ' routine');
      else parts.push('The strongest match in our serums for ' + tw + ' skin');
    } else if (step === 'cleanser') {
      parts.push((concC || suitC) ? 'A first step that supports ' + phrase(concC || suitC) : 'A gentle first step for ' + tw + ' skin');
    } else if (step === 'toner') {
      parts.push((concC || suitC) ? 'Preps skin and helps with ' + phrase(concC || suitC) : 'Preps and hydrates ' + tw + ' skin so the next steps absorb better');
    } else if (step === 'moisturizer') {
      if (light) parts.push('Lightweight moisture that won’t clog');
      else if (richFit) parts.push('A richer cream to lock moisture into dry skin');
      else parts.push('Daily moisture for ' + tw + ' skin');
    } else if (step === 'sunscreen') {
      if (ctx.spf) parts.push('You said SPF isn’t daily yet — this one is easy to actually wear');
      else if (ctx.outdoors) parts.push('You’re outdoors a lot — this one is easy to reapply through the day');
      else parts.push('The step that prevents most visible aging');
    } else if (step === 'eye-care') {
      parts.push(concC ? 'A targeted extra for ' + phrase(concC) + ' around the eyes' : 'A targeted extra for the delicate eye area');
    } else if (step === 'mask') {
      parts.push((concC || suitC) ? 'A weekly boost for ' + phrase(concC || suitC) : 'A weekly reset your routine will thank you for');
    }

    if (typeFit && step !== 'serum' && sig.indexOf('type-tag') > -1 && !parts[0].match(tw)) parts.push('suits ' + tw + ' skin');
    if (love) parts.push('with the ' + love.slice(5).replace(/-/g, ' ') + ' you love');
    if (ctx.sensitive && !patchNote && ['caution:fragrance', 'caution:essential-oil', 'caution:drying-alcohol', 'caution:strong-active'].every(function (t) { return !has(p, t); })) {
      parts.push('kept gentle for reactive skin');
    }
    var line = parts.join(', ') + '.';
    return line.charAt(0).toUpperCase() + line.slice(1);
  }

  /* answers: { type, concerns:[..1-2], sensitive, spf, love, avoid, nutAllergy, starter } */
  function recommend(answers, products) {
    var ctx = {
      type: answers.type || 'normal',
      concerns: (answers.concerns && answers.concerns.length ? answers.concerns : []).filter(function (c) { return c && c !== 'none'; }),
      sensitive: !!answers.sensitive,
      spf: !!answers.spf,           // Q5 only: does NOT wear SPF daily
      outdoors: !!answers.outdoors, // Q4: outdoors & active (reapply note, never the "you skip SPF" line)
      love: answers.love || null,
      avoid: answers.avoid || null,
      nutAllergy: !!answers.nutAllergy || answers.avoid === 'nut-allergen'
    };
    if (!ctx.concerns.length) ctx.concerns = ['hydration']; // safe default for starter/none

    var steps = {};
    ROUTINE.forEach(function (s) { steps[s] = pickStep(s, ctx, products); });

    var boosters = [];
    (BOOSTER_MAP[ctx.concerns[0]] || []).forEach(function (bstep) {
      var b = pickStep(bstep, ctx, products);
      if (b.pick) boosters.push(b);
    });

    function total(list) { var t = 0; list.forEach(function (s) { if (steps[s].pick) t += steps[s].pick.price; }); return t; }
    return {
      ctx: ctx,
      steps: steps,
      boosters: boosters,
      starter: !!answers.starter,
      tiers: {
        simple3: { steps: SIMPLE.slice(), total: total(SIMPLE) },
        essential3: { steps: ESSENTIAL.slice(), total: total(ESSENTIAL) },
        complete5: { steps: ROUTINE.slice(), total: total(ROUTINE) }
      }
    };
  }

  return { recommend: recommend, ROUTINE: ROUTINE, ESSENTIAL: ESSENTIAL, SIMPLE: SIMPLE, BOOSTER_MAP: BOOSTER_MAP, CONCERN_PHRASE: CONCERN_PHRASE };
});
